import { KeyboardEventHandler, ReactElement, ReactNode } from 'react'
import styled, { css } from 'styled-components'
import { CSSProperties } from 'react'
import { I18n, Translate } from 'common'
import { NumericFormat } from 'react-number-format'

interface TextInputModel {
  value: string
  label?: string
  placeholder?: string
  onChange?: (value: string) => void
  onBlur?: (value: string) => void
  icon?: ReactNode
  style?: CSSProperties
  disabled?: boolean
  type?: 'password' | 'text' | 'currency' | 'tel'
  width?: string
  onKeyPress?: KeyboardEventHandler<HTMLInputElement>
  noMargin?: boolean
  autoFocus?: boolean
  required?: boolean
  error?: boolean
  borderRadius?: string
  onValueChange?: (value: string) => void
}

export const TextInput = (props: TextInputModel): ReactElement => {
  const {
    onChange = () => undefined,
    onBlur = () => undefined,
    placeholder = '',
    label = '',
    icon = null,
    disabled = false,
    type = 'text',
    width = '200px',
    noMargin = false,
    autoFocus = false,
    required = false,
    error = false,
    onValueChange = () => undefined,
    borderRadius = '4px',
    style,
    ...restOfProps
  } = props

  return (
    <Wrapper width={width} noMargin={noMargin} className="text-input-wrapper">
      <Label>
        <I18n name={label} />
        {required ? '*' : ''}
      </Label>
      <InputWrapper
        disabled={disabled}
        noMargin={noMargin}
        width={width}
        error={error}
        borderRadius={borderRadius}
      >
        {icon && <IconWrapper>{icon}</IconWrapper>}
        {type === 'currency' && (
          <NumericInput
            onChange={(e) => onChange(e.target.value)}
            placeholder={Translate(placeholder)}
            disabled={disabled}
            decimalScale={2}
            prefix="$"
            fixedDecimalScale
            allowNegative={false}
            autoFocus={autoFocus}
            borderRadius={borderRadius}
            onValueChange={(values) => {
              onValueChange(values.floatValue.toString())
            }}
            {...restOfProps}
            style={style}
          />
        )}

        {(type === 'password' || type === 'text') && (
          <Input
            type={type}
            onChange={(e) => onChange(e.target.value)}
            onBlur={(e) => onBlur(e.target.value)}
            placeholder={Translate(placeholder)}
            disabled={disabled}
            autoFocus={autoFocus}
            borderRadius={borderRadius}
            {...restOfProps}
            style={style}
          />
        )}
        {type === 'tel' && (
          <NumericInput
            type={type}
            onChange={(e) => onChange(e.target.value)}
            placeholder={Translate(placeholder)}
            disabled={disabled}
            allowNegative={false}
            decimalScale={0}
            autoFocus={autoFocus}
            borderRadius={borderRadius}
            {...restOfProps}
            style={style}
          />
        )}
      </InputWrapper>
    </Wrapper>
  )
}

interface WrapperProps {
  width: string
  noMargin: boolean
}

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: column;
  margin: ${(p) => (p.noMargin ? '0' : '10px')};
  width: ${(p) => p.width};
`
const Label = styled.div`
  font-family: Roboto, serif;
  font-size: 14px;
  line-height: 24px;
  align-self: flex-start;
  margin-left: 5px;
  font-weight: bold;
`

const IconWrapper = styled.span`
  display: inline-block;
  margin-left: 16px;
  margin-right: 16px;
`

const InputStyle = css<{ borderRadius?: string }>`
  display: inline-block;
  border: none;
  padding-left: 16px;
  padding-right: 16px;
  border-radius: ${(p) => p.borderRadius ?? '4px'};
  height: 48px;
  width: 100%;
  caret-color: ${(p) => p.theme.colors.primary};
`
const Input = styled.input<{ borderRadius?: string }>`
  ${InputStyle}
`

const NumericInput = styled(NumericFormat)<{ borderRadius?: string }>`
  ${InputStyle}
`

interface InputWrapperProps {
  disabled: boolean
  noMargin: boolean
  width: string
  error: boolean
  borderRadius: string
}

const InputWrapper = styled.span<InputWrapperProps>`
  display: flex;
  align-items: center;
  border: 1px solid
    ${(p) => (p.error ? p.theme.colors.red : p.theme.colors.border)};
  border-radius: ${(p) => p.borderRadius};
  color: ${(p) => p.theme.colors.text};
  margin: ${(p) => (p.noMargin ? '0' : '5px')};
  min-width: ${(p) => p.width || '200px'};
  background-color: ${(p) =>
    p.disabled ? p.theme.colors.disabledBackground : '#FFF'};
`
